import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { showApiKeysCreateDrawer } from "components/ApiKeys/CreateDrawer";
import { showApiKeysDetailsDrawer } from "components/ApiKeys/DetailsDrawer";
import ApiKeysEmptyList from "components/ApiKeys/EmptyList";
import { showApiKeysInfoDrawer } from "components/ApiKeys/InfoDrawer";
import ApiKeysList from "components/ApiKeys/List";
import ApiKeysSortHeader from "components/ApiKeys/SortHeader";
import { ApiKeyLegacyListView } from "components/ApiKeys/types";
import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { AnalyticsPagePersonal } from "hooks/useAnalytics/pages/personal";
import useErrorHandle from "hooks/useErrorHandle";
import { AuthorizationScheme } from "types/generated";
import { UserManagementActivationStatus } from "views/Account/Settings/types";

import PersonalSettingsApiKeysPageLayout from "../components/PageLayout";
import { GET_PERSONAL_API_KEYS_LEGACY } from "./gql";

type PersonalSettingsApiKeysLegacyProps = {
  authorizationScheme: string;
};

const REFETCH_QUERIES = [GET_PERSONAL_API_KEYS_LEGACY];

const PersonalSettingsApiKeysLegacy = ({
  authorizationScheme,
}: PersonalSettingsApiKeysLegacyProps) => {
  const { data, loading, error } = useQuery(GET_PERSONAL_API_KEYS_LEGACY, {
    fetchPolicy: "no-cache",
  });

  const activationStatus =
    authorizationScheme === AuthorizationScheme.ManagedUsers
      ? UserManagementActivationStatus.ACTIVE
      : UserManagementActivationStatus.INACTIVE;

  const handleOpenInfoDrawer = () => {
    showApiKeysInfoDrawer({
      analyticsPage: AnalyticsPagePersonal.PersonalApiKeys,
    });
  };

  const handleApiKeyOpenDetails = (apiKey: ApiKeyLegacyListView) => {
    showApiKeysDetailsDrawer({
      id: apiKey.id,
      apiKey,
      activationStatus,
      analyticsPage: AnalyticsPagePersonal.PersonalApiKeys,
    });
  };

  const handleCreateApiKeyDrawer = () => {
    showApiKeysCreateDrawer({
      activationStatus,
      refetchQueries: REFETCH_QUERIES,
      analyticsPage: AnalyticsPagePersonal.PersonalApiKeys,
    });
  };

  const apiKeys = useMemo(() => data?.viewer?.apiKeys || [], [data?.viewer?.apiKeys]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.viewer?.apiKeys) {
    return (
      <PersonalSettingsApiKeysPageLayout>
        <PageLayoutSkeleton />
      </PersonalSettingsApiKeysPageLayout>
    );
  }

  if (!data?.viewer?.apiKeys) {
    return <NotFoundPage />;
  }

  return (
    <PersonalSettingsApiKeysPageLayout
      onOpenInfoDrawer={handleOpenInfoDrawer}
      onOpenCreateApiKeyDrawer={handleCreateApiKeyDrawer}
      seats={data.seats.apiKey}
      showOrganizationSettingsCallout
    >
      {apiKeys.length === 0 && <ApiKeysEmptyList />}
      {apiKeys.length > 0 && (
        <>
          <ApiKeysSortHeader />
          <ApiKeysList
            apiKeys={apiKeys}
            handleApiKeyOpenDetails={handleApiKeyOpenDetails}
            refetchQueriesOnDelete={REFETCH_QUERIES}
          />
        </>
      )}
    </PersonalSettingsApiKeysPageLayout>
  );
};

export default PersonalSettingsApiKeysLegacy;
